import { Link, useLocation } from 'react-router';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Fragment } from 'react';
import { IoMdHome } from 'react-icons/io';
import { getBreadcrumbFromPath } from '@/lib/utils';
import { useMachinesNames } from '@/hooks/useMachinesNames';
import { useEnergyDevices } from '@/hooks/useEnergy';
import { useFridges, useTunnels } from '@/hooks/useFrigo';

export default function AppBreadcrumb() {
  const { pathname } = useLocation();
  const pathSegments = pathname.split('/').filter(Boolean);
  const rootSegment = pathSegments[0];
  const detailSegment = pathSegments[1];

  const shouldLoadMachinesNames = rootSegment === 'machines' && pathSegments.some(isObjectIdSegment);
  const shouldLoadEnergyDevices = rootSegment === 'energie' && Boolean(detailSegment);
  const shouldLoadColdStorageUnits = rootSegment === 'frigos' && Boolean(detailSegment);
  const { data: machinesNames } = useMachinesNames(shouldLoadMachinesNames);
  const { query: energyDevicesQuery } = useEnergyDevices({ enabled: shouldLoadEnergyDevices });
  const { query: tunnelsQuery } = useTunnels({ enabled: shouldLoadColdStorageUnits });
  const { query: fridgesQuery } = useFridges({ enabled: shouldLoadColdStorageUnits });

  const breadcrumbs = pathSegments.map((b, index) => {
    const link = pathSegments.slice(0, index + 1).join('/');
    const breadcrumb = {
      name: b,
      link,
    };

    if (rootSegment === 'energie' && index === 1) {
      const energyDevice = energyDevicesQuery.data?.find((device) => device.id === b);
      return energyDevice ? { name: formatEnergyDeviceName(energyDevice.name), link } : breadcrumb;
    }

    if (rootSegment === 'frigos' && index === 1) {
      const coldStorageUnit =
        tunnelsQuery.data?.find((unit) => unit.id === b) || fridgesQuery.data?.find((unit) => unit.id === b);
      return coldStorageUnit ? { name: coldStorageUnit.name, link } : breadcrumb;
    }

    if (isObjectIdSegment(b)) {
      const machine = machinesNames?.find((m) => m._id === b);
      const machineBreadCrumb = {
        name: machine?.name,
        link,
      };
      return machine ? machineBreadCrumb : breadcrumb;
    }
    return breadcrumb;
  });
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <Link to="/dashboard">
            <BreadcrumbLink asChild>
              <IoMdHome />
            </BreadcrumbLink>
          </Link>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        {breadcrumbs.slice(0, -1).map((breadcrumb) => (
          <Fragment key={breadcrumb.name}>
            <BreadcrumbItem className="text-xs font-medium">
              <Link to={'/' + breadcrumb.link}>{getBreadcrumbFromPath(breadcrumb.name ?? '')}</Link>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
          </Fragment>
        ))}

        <BreadcrumbItem>
          <BreadcrumbPage className="text-azura-blue-dark text-xs font-semibold">
            {getBreadcrumbFromPath(breadcrumbs.at(-1)?.name ?? '')}
          </BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function isObjectIdSegment(segment: string) {
  return segment.length === 24 && !segment.includes(' ');
}

function formatEnergyDeviceName(name: string) {
  return name.replace(/\s+(?:\u00c9nergie|Energie|\u00c3\u2030nergie)$/i, '').trim();
}
